<template>
  <Card
    noBorder
  >
    <SectionHeader
      title="Insights"
    >
      {{ $options.instructions.general }}
    </SectionHeader>
    <Card :class="$style.card">
      <template v-slot:header>
        <header
          :class="$style.cardHeader"
        >
          <HeadingSmall :hasMargin="false">
            {{ directoryInsightsTitle }}
          </HeadingSmall>
          <LearnMoreLink
            :featureCode="$options.directoryProductCode"
            :featureName="$options.directoryLabel"
          />
        </header>
      </template>
      <template v-slot:body>
        <ParagraphBody
          :class="$style.description"
        >
          {{ $options.instructions.directory }}
        </ParagraphBody>
        <ParagraphBody
          v-if="!directoryInsightsEnabled"
          :class="$style.description"
        >
          <MarkdownInline
            :mdTemplate="$options.instructions.directoryLimits"
          />
        </ParagraphBody>
      </template>
    </Card>
    <Card :class="$style.card">
      <template v-slot:header>
        <header
          :class="$style.cardHeader"
        >
          <ToggleSwitch
            v-model="systemInsightsEnabled"
            :label="$options.switchLabels.system"
            :labelOn="$options.switchLabels.systemOn"
          />
          <LearnMoreLink
            :featureCode="$options.systemProductCode"
            :featureName="$options.systemLabel"
          />
        </header>
      </template>
      <template v-slot:body>
        <div>
          <ParagraphBody
            :class="$style.description"
            data-test-id="SectionInsights__systemInsightsText"
          >
            {{ systemInsightsLabel }}
          </ParagraphBody>
          <SystemInsightsFields
            v-model="systemInsights"
            :disabled="!systemInsightsEnabled"
          />
          <ModalSystemInsights
            data-test-id="insightsConfirmationModal"
            :enableSystemInsights="systemInsightsEnabled"
            :showModal="showModal"
            @cancel="handleCancel"
            @close="closeModal"
            @save="handleContinue"
          />
        </div>
      </template>
    </Card>
  </Card>
</template>

<script>
import {
  Card,
  HeadingSmall,
  MarkdownInline,
  ParagraphBody,
  ToggleSwitch,
} from '@jumpcloud/ui-components';
import { get } from 'lodash';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import LearnMoreLink from 'client/settings/FeatureSettings/LearnMoreLink.vue';
import ModalSystemInsights from 'client/settings/FeatureSettings/ModalSystemInsights.vue';
import SectionHeader from 'client/settings/components/SectionHeader.vue';
import SystemInsightsFields from 'client/settings/FeatureSettings/FieldsSystemInsights.vue';

const instructions = {
  directory: 'Directory Insights provides centralized data on user and directory activity so you can audit, report on, and investigate events across users, devices, and resources in JumpCloud.',
  directoryLimits: 'Directory Insights is limited to the **last 15** days of events. To upgrade to the last 90 days of events, see plan options.',
  general: 'Quickly see directory activity and system intelligence data across your organization with Directory Insights and System Insights.',
  system: 'System Insights provides fleet-wide inventory, security, and configuration intelligence.',
  systemOff: 'Try System Insights free for 30 days.',
};

const directoryLabel = 'Directory Insights';
const directoryProductCode = 'directoryInsightsPremium';
const kbArticleLink = KbArticleUrls.directoryInsights;
const systemLabel = 'System Insights';
const systemProductCode = 'systemInsights';

const switchLabels = {
  directory: `${directoryLabel} Limited`,
  directoryOn: `${directoryLabel} On`,
  system: `${systemLabel} Off`,
  systemOn: `${systemLabel} On`,
};

export default {
  name: 'SectionInsights',

  components: {
    Card,
    HeadingSmall,
    LearnMoreLink,
    MarkdownInline,
    ParagraphBody,
    ModalSystemInsights,
    SectionHeader,
    SystemInsightsFields,
    ToggleSwitch,
  },

  props: {
    value: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      showModal: false,
    };
  },

  computed: {
    directoryInsightsEmail() {
      return this.directoryInsightsEnabled
        ? 'accounts@jumpcloud.com' : 'directoryinsights@jumpcloud.com';
    },

    directoryInsightsEnabled: {
      get() {
        return get(this.value, 'features.directoryInsightsPremium.enabled', false);
      },
      set(newValue) {
        this.$emit('input', {
          ...this.value,
          features: {
            directoryInsightsPremium: {
              enabled: newValue,
            },
          },
        });
      },
    },

    directoryInsightsTitle() {
      const { directory, directoryOn } = switchLabels;

      return this.directoryInsightsEnabled ? directoryOn : directory;
    },

    systemInsights: {
      get() {
        return get(this.value, 'features.systemInsights', {});
      },
      set(newValue) {
        this.$emit('input', {
          ...this.value,
          features: {
            ...this.value.features,
            systemInsights: {
              ...newValue,
            },
          },
        });
      },
    },

    systemInsightsEnabled: {
      get() {
        return get(this.value, 'features.systemInsights.enabled', false);
      },
      set(newValue) {
        this.updateSystemInsights(newValue);
        this.openModal();
      },
    },

    systemInsightsLabel() {
      let label = `${instructions.systemOff} ${instructions.system}`;

      if (this.systemInsightsEnabled) {
        label = instructions.system;
      }
      return label;
    },
  },

  directoryLabel,
  directoryProductCode,
  instructions,
  kbArticleLink,
  switchLabels,
  systemLabel,
  systemProductCode,

  methods: {
    closeModal() {
      this.showModal = false;
    },

    handleCancel() {
      this.updateSystemInsights(!this.systemInsightsEnabled);
      this.closeModal();
    },

    handleContinue() {
      this.updateSystemInsights(this.systemInsightsEnabled);
      this.closeModal();
    },

    openModal() {
      this.showModal = true;
    },

    updateSystemInsights(newValue) {
      this.$emit('input', {
        ...this.value,
        features: {
          ...this.value.features,
          systemInsights: {
            enabled: newValue,
            enableNewDarwin: newValue,
            enableNewLinux: newValue,
            enableNewWindows: newValue,
          },
        },
      });
    },
  },
};
</script>

<style module scoped>
.card {
  --text-max-width: 75ch;
}

.card:not(:last-child) {
  margin-bottom: var(--jc-spacer);
}

.cardHeader {
  --switch-label-align: center;
  --switch-label-size: var(--jcBodyLarge);
  --switch-label-weight: 700;

  display: flex;
  flex-wrap: wrap;
  gap: var(--jc-spacer);
}

.description {
  max-width: var(--text-max-width);
}
</style>
